import { MakerFlag } from '../../types/entry.types';

// Display-side mirrors of `RateCalculator.netTakerAsk` /
// `calculatePositionTna` for the taker-taker BUY scenario on the opener.
// Kept in sync with `arbitrage-core/services/position-opener/src/constants.ts`.
export const KALSHI_TAKER_FEE_RATE = 0.07;
export const polymarketTakerFeeRate = (category?: string): number => {
  if (category === 'sports') return 0.03;
  if (category === 'crypto') return 0.072;
  return 0.072;
};
export const allInKalshiAsk = (raw: number): number =>
  raw + KALSHI_TAKER_FEE_RATE * raw * (1 - raw);
export const allInPolymarketAsk = (raw: number, category?: string): number => {
  const rate = polymarketTakerFeeRate(category);
  return raw + rate * raw * (1 - raw);
};

// Platform price ticks — used to compute "real" maker rest price as
// (best bid + 1 tick), which is where the engine would actually post
// to be top-of-queue.
export const POLY_TICK = 0.001;
export const KALSHI_TICK = 0.01;

export const MIN_DAYS = 1 / 24;

export const fmtPrice = (v: number | undefined | null): string => {
  if (v == null || isNaN(v)) return '—';
  if (v === 0) return '0.000';
  if (v >= 0.01) return v.toFixed(3);
  return v.toFixed(4);
};

export const fmtQty = (v: number | undefined | null): string => {
  if (v == null || isNaN(v)) return '—';
  if (v <= 0) return '—';
  if (v >= 1_000_000) return `${Math.floor(v / 100_000) / 10}M`;
  if (v >= 1_000) return `${Math.floor(v / 100) / 10}k`;
  if (v >= 100) return String(Math.floor(v));
  return v.toFixed(2);
};

export const fmtPct = (v: number | null): string => {
  if (v == null || !Number.isFinite(v)) return '—';
  return `${v >= 0 ? '+' : ''}${v.toFixed(1)}%`;
};

// Continuous-compounding TNA for a path where BOTH legs are bought.
// `sum` is the all-in BUY cost. Returns null when the sum is non-positive
// or the event has fully expired (matches the backend's `MIN_DAYS = 1/24`
// floor + −1 cutoff).
export const takerTnaFromSum = (
  sum: number | null,
  daysToExpiry: number | null | undefined,
): number | null => {
  if (sum == null || sum <= 0 || daysToExpiry == null) return null;
  if (daysToExpiry < -1) return null;
  const days = Math.max(MIN_DAYS, daysToExpiry);
  const profit = 1 / sum - 1;
  return (Math.log(1 + profit) / (days / 365)) * 100;
};

// Each tab governs one of the two entry paths. Tab A = poly YES +
// kalshi NO; Tab B = poly NO + kalshi YES. The poly/kalshi maker switch
// on each tab maps to that path's two maker flags; the TT switch is
// shared (`entryStrategy` is a single rule field) but binds to the
// tab's sides on first activation.
export const TAB_FLAGS: Record<
  'a' | 'b',
  { poly: MakerFlag; kalshi: MakerFlag }
> = {
  a: { poly: 'polyYesMaker', kalshi: 'kalshiNoMaker' },
  b: { poly: 'polyNoMaker', kalshi: 'kalshiYesMaker' },
};

// Side persisted onto a newly-created rule for the given maker flag.
// The backend re-runs the same derivation in RulesService; the values
// here just give it a head start.
export const FLAG_TO_SIDES: Record<
  MakerFlag,
  { polySide: 'yes' | 'no'; kalshiSide: 'yes' | 'no' }
> = {
  polyYesMaker: { polySide: 'yes', kalshiSide: 'no' },
  kalshiNoMaker: { polySide: 'yes', kalshiSide: 'no' },
  polyNoMaker: { polySide: 'no', kalshiSide: 'yes' },
  kalshiYesMaker: { polySide: 'no', kalshiSide: 'yes' },
};
